/**
 * Criterion Search Patterns Utility
 * Extracts relevant search keywords from criterion names and feedback
 * to automatically search student code when a criterion is clicked
 */

interface CriterionSearchPattern {
  keywords: string[];
  suggestion: string; // Human-readable suggestion for what to search
}

/**
 * Extract searchable keywords from criterion name and feedback
 */
export const extractSearchKeywords = (
  criterionName: string,
  feedback?: string
): CriterionSearchPattern => {
  const keywords: string[] = [];
  let suggestion = '';

  const nameLower = criterionName.toLowerCase();
  const feedbackLower = feedback?.toLowerCase() || '';
  const combined = `${nameLower} ${feedbackLower}`;

  // Variable-related patterns
  if (combined.includes('variable') || combined.includes('var ') || combined.includes('let ') || combined.includes('const ')) {
    // Try to extract variable names from feedback
    const varMatches = feedback?.match(/\b(var|let|const)\s+(\w+)/gi);
    if (varMatches) {
      varMatches.forEach(match => {
        const varName = match.split(/\s+/)[1];
        if (varName) keywords.push(varName);
      });
      suggestion = `Variables: ${keywords.join(', ')}`;
    } else {
      keywords.push('var ', 'let ', 'const ');
      suggestion = 'Variable declarations';
    }
  }

  // Function-related patterns
  if (combined.includes('function') || combined.includes('method')) {
    const funcMatches = feedback?.match(/\b(function\s+)?(\w+)\s*\(/gi);
    if (funcMatches) {
      funcMatches.forEach(match => {
        const funcName = match.replace(/function\s+/, '').replace(/\(.*/, '').trim();
        if (funcName && !['if', 'while', 'for'].includes(funcName)) {
          keywords.push(funcName);
        }
      });
      suggestion = keywords.length > 0 ? `Functions: ${keywords.join(', ')}` : 'Function declarations';
    } else {
      keywords.push('function');
      suggestion = 'Function declarations';
    }
  }

  // Loop-related patterns
  if (combined.includes('loop') || combined.includes('iteration')) {
    keywords.push('for', 'while', 'forEach');
    suggestion = 'Loops and iterations';
  }

  // Conditional-related patterns
  if (combined.includes('conditional') || combined.includes('if statement') || combined.includes('if-else')) {
    keywords.push('if', 'else');
    suggestion = 'Conditional statements';
  }

  // Comment-related patterns
  if (combined.includes('comment') || combined.includes('documentation')) {
    keywords.push('//', '/*', '*/', '<!--');
    suggestion = 'Comments and documentation';
  }

  // HTML element patterns
  if (combined.includes('html') || combined.includes('element') || combined.includes('tag')) {
    const elementMatches = feedback?.match(/<(\w+)>/gi);
    if (elementMatches) {
      elementMatches.forEach(match => {
        const element = match.replace(/[<>]/g, '');
        keywords.push(`<${element}`);
      });
      suggestion = `HTML elements: ${keywords.join(', ')}`;
    }
  }

  // CSS-related patterns
  if (combined.includes('css') || combined.includes('style') || combined.includes('styling')) {
    const cssMatches = feedback?.match(/\b(color|background|font|margin|padding|border|width|height)[\s:]/gi);
    if (cssMatches) {
      cssMatches.forEach(match => {
        const property = match.replace(/[\s:]/g, '');
        keywords.push(property);
      });
      suggestion = `CSS properties: ${keywords.join(', ')}`;
    }
  }

  // Class/Object-related patterns
  if (combined.includes('class') || combined.includes('object')) {
    keywords.push('class ', 'new ');
    suggestion = 'Classes and objects';
  }

  // Array-related patterns
  if (combined.includes('array') || combined.includes('list')) {
    keywords.push('[', 'Array', '.push', '.pop', '.map');
    suggestion = 'Arrays and array methods';
  }

  // Error handling patterns
  if (combined.includes('error') || combined.includes('exception') || combined.includes('try')) {
    keywords.push('try', 'catch', 'throw');
    suggestion = 'Error handling';
  }

  // If no specific patterns found, try to extract quoted words from feedback
  if (keywords.length === 0 && feedback) {
    const quotedMatches = feedback.match(/"([^"]+)"|'([^']+)'/g);
    if (quotedMatches) {
      quotedMatches.forEach(match => {
        const cleaned = match.replace(/['"]/g, '');
        if (cleaned.length > 2) keywords.push(cleaned);
      });
      suggestion = `Mentioned code: ${keywords.join(', ')}`;
    }
  }

  // If still no keywords, use the criterion name itself
  if (keywords.length === 0) {
    // Extract meaningful words from criterion name (skip numbers and common words)
    const words = criterionName.split(/[\s\-_]+/).filter(w =>
      w.length > 2 && !/^\d+\.?\d*$/.test(w) && !['the', 'and', 'for', 'with'].includes(w.toLowerCase())
    );
    keywords.push(...words);
    suggestion = `Search for: ${criterionName}`;
  }

  return {
    keywords: Array.from(new Set(keywords)), // Remove duplicates
    suggestion
  };
};

/**
 * Extract question number from criterion name
 * Examples: "1.1 Variables" -> 1, "Question 2.3" -> 2, "Q3.1" -> 3
 */
export const extractQuestionNumber = (criterionName: string): number | null => {
  // Try to match patterns like "1.1", "2.3", "Q1.1", "Question 1.1", etc.
  const patterns = [
    /^(\d+)\.\d+/,           // Matches "1.1", "2.3" at start
    /[Qq]uestion\s+(\d+)/,   // Matches "Question 1", "question 2"
    /[Qq](\d+)/,             // Matches "Q1", "q2"
  ];

  for (const pattern of patterns) {
    const match = criterionName.match(pattern);
    if (match && match[1]) {
      return parseInt(match[1], 10);
    }
  }

  return null;
};

/**
 * Get the best keyword to search based on the criterion
 */
export const getBestSearchKeyword = (criterionName: string, feedback?: string): string => {
  const pattern = extractSearchKeywords(criterionName, feedback);
  // Return the first keyword, or the criterion name if no keywords found
  return pattern.keywords[0] || criterionName;
};
